import React, { useRef, useEffect, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FaCog,
  FaTrash,
  FaCopy,
  FaEdit,
  FaPalette,
  FaThumbtack,
  FaColumns,
  FaPlus,
  FaSave,
} from "react-icons/fa";
import { Dashboard } from "@/models/dashboard";
import { useSidebarContext } from "@/hooks/use-sidebar";
import ColorPicker from "./color-picker";
import { ContextMenuItem, ContextMenuDivider } from "./context-menu-helpers";
import { MotionDiv } from "./motion-types";
import logger from "@/utils/logger";
import { Z_INDEX } from "@/utils/z-index";
import { useDashboardNavigation } from "@/hooks/use-dashboard-navigation";
import { useSplitView } from "@/components/layout/split-view";
import { createDashboardDragPayload } from "@/utils/drag-types";

interface DashboardContextMenuProps {
  position: { x: number; y: number };
  dashboard: Dashboard;
  onClose: () => void;
}

const DashboardContextMenu: React.FC<DashboardContextMenuProps> = ({
  position,
  dashboard,
  onClose,
}) => {
  const { goToDashboard, goToSettings, goToGallery, currentDashboardId } = useDashboardNavigation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const {
    pinItem,
    getItemConfig,
    deleteDashboard,
    duplicateDashboard,
    changeDashboardColor,
    startRenamingDashboard,
    hasUnsavedChanges,
    saveDashboard,
    isSavingDashboard,
  } = useSidebarContext();

  // New split view system
  const { isActive: isSplitViewActive, panels, openWithCurrentRoute, isInSplitView } = useSplitView();

  const dashboardColor = dashboard.color || "var(--arc-accent)";
  const itemConfig = getItemConfig(dashboard.id!);
  const isPinned = itemConfig?.pinned || false;
  
  // Check if this dashboard is already in the split view
  const isDashboardInSplitView = isInSplitView("dashboard", dashboard.id);
  const canAddToSplit = !isDashboardInSplitView;
  
  // Check if dashboard has unsaved changes
  const dashboardHasUnsavedChanges = dashboard.id ? !!hasUnsavedChanges(dashboard.id) : false;

  // Adjust position to stay within viewport
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 240),
    y: Math.min(position.y, window.innerHeight - 350),
  };

  const handleConfig = useCallback(() => {
    goToSettings(dashboard.id!);
    onClose();
  }, [goToSettings, dashboard.id, onClose]);

  const handlePin = useCallback(() => {
    pinItem(dashboard.id!, "dashboard", !isPinned);
    onClose();
  }, [pinItem, dashboard.id, isPinned, onClose]);

  const handleDuplicate = useCallback(async () => {
    if (isDuplicating) return;
    setIsDuplicating(true);
    try {
      const newDashboard = await duplicateDashboard(dashboard);
      if (newDashboard?.id) {
        goToDashboard(newDashboard.id);
      }
    } catch (error) {
      logger.error("Failed to duplicate dashboard:", error);
    } finally {
      setIsDuplicating(false);
      onClose();
    }
  }, [isDuplicating, duplicateDashboard, dashboard, goToDashboard, onClose]);

  const handleRename = useCallback(() => {
    startRenamingDashboard(dashboard.id!);
    // onClose is called by startRenamingDashboard
  }, [startRenamingDashboard, dashboard.id]);

  const handleDelete = useCallback(async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteDashboard(dashboard.id!);
      goToGallery();
    } catch (error) {
      logger.error("Failed to delete dashboard:", error);
    } finally {
      setIsDeleting(false);
      onClose();
    }
  }, [isDeleting, deleteDashboard, dashboard.id, goToGallery, onClose]);

  const handleSave = useCallback(async () => {
    if (!dashboard.id || !!isSavingDashboard(dashboard.id) || !dashboardHasUnsavedChanges) return;
    
    try {
      await saveDashboard(dashboard.id);
      logger.info("Dashboard saved from context menu", { dashboardId: dashboard.id });
    } catch (error) {
      logger.error("Failed to save dashboard:", error);
    }
    onClose();
  }, [dashboard.id, isSavingDashboard, dashboardHasUnsavedChanges, saveDashboard, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }

      const isMeta = e.metaKey || e.ctrlKey;

      if (isMeta) {
        switch (e.key.toLowerCase()) {
          case ",":
            e.preventDefault();
            handleConfig();
            break;
          case "d":
            e.preventDefault();
            handleDuplicate();
            break;
          case "r":
            e.preventDefault();
            handleRename();
            break;
          case "p":
            e.preventDefault();
            handlePin();
            break;
          case "s":
            e.preventDefault();
            handleSave();
            break;
        }
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        handleDelete();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, handleConfig, handleDuplicate, handleRename, handlePin, handleDelete, handleSave]);

  const handleChangeColor = async (color: string) => {
    try {
      await changeDashboardColor(dashboard.id!, color);
      setColorPickerOpen(false);
      onClose();
    } catch (error) {
      logger.error("Failed to change dashboard color:", error);
    }
  };

  const handleOpenInSplit = () => {
    if (!dashboard.id) return;
    
    // Create drag payload for the dashboard
    const payload = createDashboardDragPayload(
      dashboard.id,
      dashboard.name || "Dashboard",
      dashboardColor
    );
    
    // Open in split view using the new system
    openWithCurrentRoute(payload);
    
    logger.debug("Opened dashboard in split view", { dashboardId: dashboard.id });
    onClose();
  };

  return (
    <>
      <MotionDiv
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.1 }}
        className="fixed bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] py-1 min-w-[220px]"
        style={{ top: adjustedPosition.y, left: adjustedPosition.x, zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU }}
      >
        {/* Save */}
        <ContextMenuItem
          onClick={handleSave}
          shortcut="⌘S"
          disabled={!dashboardHasUnsavedChanges || Boolean(dashboard.id && isSavingDashboard(dashboard.id))}
        >
          <FaSave className={dashboardHasUnsavedChanges ? "text-orange-400" : undefined} />
          {dashboard.id && !!isSavingDashboard(dashboard.id)
            ? "Sauvegarde..."
            : dashboardHasUnsavedChanges
            ? "Sauvegarder"
            : "Aucune modification"}
        </ContextMenuItem>

        <ContextMenuDivider />

        {/* Configure */}
        <ContextMenuItem onClick={handleConfig} shortcut="⌘,">
          <FaCog /> Configure
        </ContextMenuItem>

        {/* Pin/Unpin */}
        <ContextMenuItem onClick={handlePin} shortcut="⌘P">
          <FaThumbtack className={isPinned ? "text-yellow-400" : ""} />
          {isPinned ? "Unpin" : "Pin to favorites"}
        </ContextMenuItem>

        {/* Open in split view */}
        {canAddToSplit && (
          <ContextMenuItem onClick={handleOpenInSplit}>
            {isSplitViewActive ? (
              <>
                <FaPlus className="text-emerald-400" /> Add to split view ({panels.length} panels)
              </>
            ) : (
              <>
                <FaColumns className="text-indigo-400" /> Open in split view
              </>
            )}
          </ContextMenuItem>
        )}

        {/* Change color - Submenu */}
        <div className="relative">
          <ContextMenuItem
            onClick={() => setColorPickerOpen(!colorPickerOpen)}
            hasSubmenu
          >
            <FaPalette style={{ color: dashboardColor }} /> Change color
          </ContextMenuItem>

          <AnimatePresence>
            {colorPickerOpen && (
              <MotionDiv
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="absolute left-full top-0 ml-1 bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] p-3 min-w-[200px]"
              >
                <ColorPicker
                  value={dashboardColor}
                  onChange={handleChangeColor}
                  compact
                />
              </MotionDiv>
            )}
          </AnimatePresence>
        </div>

        <ContextMenuDivider />

        {/* Duplicate */}
        <ContextMenuItem
          onClick={handleDuplicate}
          shortcut="⌘D"
          disabled={isDuplicating}
        >
          <FaCopy /> {isDuplicating ? "Duplicating..." : "Duplicate"}
        </ContextMenuItem>

        {/* Rename */}
        <ContextMenuItem onClick={handleRename} shortcut="⌘R">
          <FaEdit /> Rename
        </ContextMenuItem>

        <ContextMenuDivider />

        {/* Delete */}
        <ContextMenuItem
          onClick={handleDelete}
          shortcut="⌫"
          danger
          disabled={isDeleting}
        >
          <FaTrash /> {isDeleting ? "Deleting..." : "Delete"}
        </ContextMenuItem>
      </MotionDiv>
    </>
  );
};

DashboardContextMenu.displayName = "DashboardContextMenu";

export default DashboardContextMenu;
